/**
 * Which layout the shell should render.
 *
 * ## Why a hook and not a media query
 *
 * Most of the difference between the phone and the desktop is CSS, and CSS is
 * where it belongs. But two things cannot be done in a stylesheet:
 *
 *   - the rail and the tab bar are different COMPONENTS, not one component at
 *     two widths. A tab bar with ten destinations is not a rail that shrank.
 *   - a chart that is read-only on a phone must not MOUNT its drawing layer.
 *     Hiding it with `display: none` still builds it, still binds its pointer
 *     handlers, and still ships its work to a device that cannot use it.
 *
 * ## Why matchMedia and not innerWidth
 *
 * `innerWidth` has to be sampled, which means a resize listener and a guess
 * about when to sample it. `matchMedia` fires exactly when the answer changes —
 * including on a rotation and on an iPad entering split view, both of which a
 * width poll gets wrong until something else happens to re-render.
 *
 * The breakpoint matches `--grat-step`'s in the marketing site's tokens, so the
 * two surfaces agree on where a phone starts.
 */
import { useEffect, useState } from 'react';

/** Below this, the shell is a phone. */
export const MOBILE_MAX = 767;

const QUERY = `(max-width: ${MOBILE_MAX}px)`;

/**
 * Read once, synchronously, for the first render.
 *
 * Returning `false` on the server and correcting it in an effect would mount
 * the desktop rail and then throw it away — a visible flash of the wrong shell
 * on every cold load on a phone, which is the load that matters most.
 */
function initial(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(QUERY).matches;
}

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(initial);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(QUERY);
    const onChange = (e: MediaQueryListEvent): void => setIsMobile(e.matches);

    // Safari before 14 has only the deprecated listener. Cheap to support and
    // the alternative is a phone browser stuck in the desktop layout for good.
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);

    // The query can already disagree with what was measured at module load —
    // an orientation change during the first paint, for instance.
    setIsMobile(mq.matches);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  return isMobile;
}
